import { debugLog } from "../utils/debugLog.js";
import React, { useState, useEffect, useRef } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogPanel,
  DialogBackdrop,
} from '@headlessui/react';
import { MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/20/solid';
import {
  BuildingOffice2Icon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CurrencyDollarIcon,
  QuestionMarkCircleIcon,
  BookOpenIcon,
  UserGroupIcon,
  ClockIcon,
  TableCellsIcon,
  ClipboardDocumentListIcon,
  BeakerIcon,
  FireIcon,
  BoltIcon,
  BanknotesIcon,
  UserPlusIcon,
  ChartBarSquareIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import PalmTreeIcon from './PalmTreeIcon.jsx';
import Airtable from 'airtable';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { extractDomain } from '../helpers.js';

const debugVerbose = process.env.REACT_APP_DEBUG_VERBOSE === 'true';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'companies', label: 'Companies' },
  { id: 'grants_scoped', label: 'PerfectDrafts' },
  { id: 'grants_unscoped', label: 'Grants' },
  { id: 'software_savings', label: 'Software Savings' },
];

const HELP_QUICK_LINKS = [
  {
    id: 'statement-guide',
    name: 'How success fees are calculated',
    href: '/help/how-success-fees-are-calculated',
    keywords: [
      'success fees',
      'how success fees are calculated',
      'account statement',
      'due today',
      'invoice',
      'how is this calculated',
    ],
    icon: QuestionMarkCircleIcon,
    type: 'page',
    resultKey: 'page-statement-guide',
  },
];

const ADMIN_QUICK_LINKS = [
  {
    id: 'all-opportunities',
    name: 'All Opportunities',
    href: '/all-opportunities',
    keywords: ['all opportunities', 'opportunities'],
    icon: PalmTreeIcon,
    type: 'page',
    resultKey: 'page-all-opportunities',
  },
  {
    id: 'companies',
    name: 'Companies',
    href: '/companies',
    keywords: ['companies', 'company list', 'partners'],
    icon: BuildingOffice2Icon,
    type: 'page',
    shortcut: 'u',
    resultKey: 'page-companies',
  },
];

const currentSearch = () => window.location.search || '';

const companyInfoHref = () => {
  const search = currentSearch();
  const website = new URLSearchParams(search.replace(/^\?/, '')).get('website');
  if (!website) return `/company-info-section${search}`;
  const domain = decodeURIComponent(website).split('&')[0];
  return `/company-info/${domain}${search}`;
};

const homeHashHref = (hash) => `${currentSearch() ? `/${currentSearch()}` : '/'}#${hash}`;

// Mirrors teamAdminSections in Layout.js
const TEAM_ADMIN_PAGES = [
  {
    id: 'team-guides',
    name: 'Team Guides',
    href: '/admin/team-docs',
    section: 'Guides & Docs',
    keywords: ['docs', 'documentation', 'guides'],
    icon: BookOpenIcon,
  },
  {
    id: 'customers-admin',
    name: 'Customers Admin',
    href: '/customers-admin',
    section: 'Clients & Accounts',
    keywords: ['customers', 'accounts', 'clients'],
    icon: UserGroupIcon,
  },
  {
    id: 'ar-ledger-log',
    name: 'Opportunity Ledger Log',
    href: '/admin/ar-ledger-log',
    section: 'Clients & Accounts',
    keywords: ['ledger', 'ar', 'accounts receivable', 'log'],
    icon: ClockIcon,
  },
  {
    id: 'view-workspace',
    name: 'View Workspace as User',
    href: '/workspace-selection',
    section: 'Clients & Accounts',
    keywords: ['workspace', 'view as', 'switch user', 'impersonate'],
    icon: UserGroupIcon,
    preserveSearch: false,
    onSelect: (navigate) => {
      navigate('/workspace-selection', { state: { openAdminSwitcher: true } });
    },
  },
  {
    id: 'company-info',
    name: 'Company Info',
    getHref: companyInfoHref,
    section: 'Clients & Accounts',
    keywords: ['company profile', 'profile'],
    icon: BuildingOffice2Icon,
    preserveSearch: false,
  },
  {
    id: 'grant-admin',
    name: 'Grant Admin',
    href: '/grants-admin',
    section: 'Grants & Strategy',
    keywords: ['grants admin', 'grant admin'],
    icon: TableCellsIcon,
  },
  {
    id: 'perfectdraft-creator',
    name: 'PerfectDraft Creator',
    href: '/edit-grant-questions',
    section: 'Grants & Strategy',
    keywords: ['perfect draft', 'grant questions', 'questions'],
    icon: ClipboardDocumentListIcon,
  },
  {
    id: 'sred-claims',
    name: 'SR&ED Claims',
    href: '/sred',
    section: 'Grants & Strategy',
    keywords: ['sred', 'sr&ed', 'claims', 'scientific research'],
    icon: BeakerIcon,
  },
  {
    id: 'client-leaderboard',
    name: 'Client Leaderboard',
    href: '/leaderboard',
    section: 'Grants & Strategy',
    keywords: ['leaderboard', 'clients'],
    icon: FireIcon,
  },
  {
    id: 'prospect-leaderboard',
    name: 'Prospect Leaderboard',
    href: '/prospect-leaderboard',
    section: 'Business Development',
    keywords: ['prospects', 'leaderboard', 'pipeline'],
    icon: FireIcon,
  },
  {
    id: 'automation-requests',
    name: 'Automation Requests',
    href: '/automation-requests',
    section: 'Business Development',
    keywords: ['automations', 'requests'],
    icon: BoltIcon,
  },
  {
    id: 'financing-requests',
    name: 'Financing Requests',
    href: '/financing-requests',
    section: 'Business Development',
    keywords: ['financing', 'funding', 'requests'],
    icon: BanknotesIcon,
  },
  {
    id: 'ai-automations',
    name: 'AI Automations',
    href: '/ai-automations',
    section: 'Business Development',
    keywords: ['ai', 'automations'],
    icon: BoltIcon,
    shortcut: 'i',
  },
  {
    id: 'vendor-savings-admin',
    name: 'Vendor Savings Admin',
    href: '/vendor-savings-admin',
    section: 'Software Savings',
    keywords: ['vendor', 'discounts', 'software savings'],
    icon: CurrencyDollarIcon,
  },
  {
    id: 'team-earnings',
    name: 'Team Earnings',
    getHref: () => homeHashHref('team-pipeline'),
    section: 'Performance & Reports',
    keywords: ['earnings', 'pipeline', 'team pipeline'],
    icon: UserPlusIcon,
    preserveSearch: false,
  },
  {
    id: 'my-reports',
    name: 'My Reports',
    getHref: () => homeHashHref('my-reports'),
    section: 'Performance & Reports',
    keywords: ['reports'],
    icon: ChartBarSquareIcon,
    preserveSearch: false,
  },
  {
    id: 'public-leaderboard',
    name: 'Leaderboard',
    href: '/public-leaderboard',
    section: 'Performance & Reports',
    keywords: ['public leaderboard', 'leaderboard'],
    icon: TrophyIcon,
  },
].map((page) => ({
  ...page,
  type: 'page',
  adminPage: true,
  resultKey: `page-${page.id}`,
}));

const linkSearchTerms = (link) => [
  link.name,
  link.section,
  ...(link.keywords || []),
].filter(Boolean).map((term) => term.toLowerCase());

const matchesQuickLink = (link, searchQuery) => {
  const q = searchQuery.toLowerCase().trim();
  if (q.length < 2) return false;

  const terms = linkSearchTerms(link);
  const queryWords = q.split(/\s+/).filter(Boolean);

  return terms.some((term) => {
    if (term.includes(q)) return true;
    return queryWords.length > 0 && queryWords.every((word) => term.includes(word));
  });
};

const matchesTeamAdminPage = (link, searchQuery) => {
  const q = searchQuery.toLowerCase().trim();
  if (!q) return true;
  return linkSearchTerms(link).some((term) => term.includes(q));
};

const CommandPalette = ({ isOpen, onClose, admin = false }) => {
  const [query, setQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [grants, setGrants] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userCompanyId, setUserCompanyId] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [copiedItemId, setCopiedItemId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const dialogRef = useRef(null);
  const optionsRef = useRef(null);
  const inputRef = useRef(null);
  const { user } = useUser();
  const navigate = useNavigate();

  // Helper to generate URL slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return String(title)
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Helper to append UTM params for command palette attribution
  const appendUtmParams = (url) => {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}utm_source=command_palette&utm_medium=search`;
  };

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, companies, grants, discounts, activeTab]);

  // Get shareable URL for an item
  const getItemUrl = (item) => {
    const baseUrl = window.location.origin;
    const current = new URLSearchParams(window.location.search);
    const scopedWebsite = item.website || current.get('website') || '';
    const scopedCompany = item.company || current.get('company') || '';
    const withCompanyScope = (path) => {
      if (path.includes('website=') || path.includes('company=')) return path;
      const params = new URLSearchParams();
      if (scopedWebsite) params.set('website', scopedWebsite);
      if (scopedCompany) params.set('company', scopedCompany);
      const qs = params.toString();
      if (!qs) return path;
      return `${path}${path.includes('?') ? '&' : '?'}${qs}`;
    };
    if (item.type === 'page') {
      const path = typeof item.getHref === 'function' ? item.getHref() : item.href;
      const scoped = item.preserveSearch === false ? path : withCompanyScope(path);
      return appendUtmParams(`${baseUrl}${scoped}`);
    }
    if (item.type === 'software_saving') {
      return appendUtmParams(`${baseUrl}/software-savings/${item.slug}`);
    }
    if (item.type === 'company') {
      if (item.destination === 'software_savings') {
        return appendUtmParams(`${baseUrl}/software-savings?website=${encodeURIComponent(item.website)}&company=${encodeURIComponent(item.name)}`);
      }
      if (item.destination === 'statement_guide') {
        return appendUtmParams(`${baseUrl}/help/how-success-fees-are-calculated?website=${encodeURIComponent(item.website)}&company=${encodeURIComponent(item.name)}`);
      }
      return appendUtmParams(`${baseUrl}/grants-section?website=${encodeURIComponent(item.website)}&company=${encodeURIComponent(item.name)}`);
    }
    if (item.type === 'grant') {
      if (item.grantScope === 'unscoped') {
        // Record id in the URL (stable). Legacy /grant/{name-slug} URLs still work on the API.
        return appendUtmParams(`${baseUrl}/grant/${encodeURIComponent(item.grantId || item.id)}`);
      }
      const companyId = userCompanyId || item.companyId;
      return appendUtmParams(`${baseUrl}/enhanced-grants-form/${companyId}/${item.grantId}`);
    }
    return appendUtmParams(baseUrl);
  };

  const handleCopyLink = async (e, item) => {
    e.stopPropagation();
    e.preventDefault();
    const url = getItemUrl(item);
    try {
      await navigator.clipboard.writeText(url);
      const itemKey = item.resultKey || `${item.type}-${item.id}`;
      setCopiedItemId(itemKey);
      setTimeout(() => setCopiedItemId(null), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Initialize Airtable base - move outside component to prevent recreation
  const apiKey = process.env.REACT_APP_AIR_API_KEY;
  const baseId = process.env.REACT_APP_AIR_BASEID;
  const base = React.useMemo(() => new Airtable({ apiKey }).base(baseId), [apiKey, baseId]);

  // Function to get company logo from LogoUrl field or generate from domain
  const getCompanyLogo = (company) => {
    // First, check if there's a LogoUrl field
    if (company.fields?.LogoUrl) {
      return company.fields.LogoUrl;
    }
    
    // If no LogoUrl, try to generate from domain
    const domain = company.fields?.Domain;
    if (!domain) return null;
    
    // Normalize the domain (remove protocol, www, etc.)
    const normalizedDomain = domain
      .toLowerCase()
      .replace(/^https?:\/\//, '') // Remove http:// or https://
      .replace(/^www\./, '') // Remove www.
      .replace(/\/$/, '') // Remove trailing slash
      .split('/')[0]; // Take only the domain part (before any path)

    const iconUrl = `https://img.logo.dev/${normalizedDomain}?token=pk_acnHOju_S3KawCcUE6Ek2Q`;
    
    return iconUrl;
  };

  // Function to fetch user's company ID based on their email domain
  const fetchUserCompanyId = async () => {
    if (!user?.primaryEmailAddress?.emailAddress) return;
    
    try {
      const userDomain = extractDomain(user.primaryEmailAddress.emailAddress);
      if (debugVerbose) debugLog('🔍 Looking for company with domain:', userDomain);
      
      const companyRecords = await base('companies')
        .select({
          filterByFormula: `{Domain} = '${userDomain}'`,
          maxRecords: 1
        })
        .all();
      
      if (companyRecords.length > 0) {
        setUserCompanyId(companyRecords[0].id);
        if (debugVerbose) debugLog('✅ Found user company ID:', companyRecords[0].id);
      } else if (debugVerbose) {
        debugLog('❌ No company found for domain:', userDomain);
      }
    } catch (error) {
      console.error('Error fetching user company:', error);
    }
  };

  // Defer fetch until palette is opened - avoids Airtable call on initial page load
  useEffect(() => {
    if (isOpen && user?.primaryEmailAddress?.emailAddress) {
      fetchUserCompanyId();
    }
  }, [isOpen, user]);

  // Handle escape key, arrow navigation, and click outside
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        debugLog('Escape key pressed, closing command palette');
        onClose();
        setQuery('');
        return;
      }

      // ArrowDown/ArrowUp: cycle through options and update highlight
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && optionsRef.current) {
        const options = Array.from(optionsRef.current.querySelectorAll('[role="option"]'));
        if (options.length === 0) return;

        event.preventDefault();

        setHighlightedIndex((prev) => {
          let next;
          if (event.key === 'ArrowDown') {
            next = prev < options.length - 1 ? prev + 1 : 0;
          } else {
            next = prev > 0 ? prev - 1 : options.length - 1;
          }
          // Focus the option so Headless UI tracks it for Enter key selection
          requestAnimationFrame(() => {
            const opt = optionsRef.current?.querySelectorAll('[role="option"]')[next];
            opt?.focus();
          });
          return next;
        });
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, { capture: true });
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [isOpen, onClose]);

  const discountsTableId = 'discounts copy';

  // Fetch companies, grants, and discounts based on query
  useEffect(() => {
    if (!query || query.length < 2) {
      setCompanies([]);
      setGrants([]);
      setDiscounts([]);
      return;
    }

    const searchData = async () => {
      setLoading(true);
      try {
        const searchTerm = query.toLowerCase().replace(/"/g, '\\"');
        const searchPromises = [];

        // Search companies (for all, companies, or software_savings with company fallback)
        if (['all', 'companies'].includes(activeTab)) {
          searchPromises.push(
            base('companies')
              .select({
                filterByFormula: `OR(
                  FIND(LOWER("${searchTerm}"), LOWER({Name})),
                  FIND(LOWER("${searchTerm}"), LOWER({Domain}))
                )`,
                maxRecords: 10
              })
              .all()
          );
        } else {
          searchPromises.push(Promise.resolve([]));
        }

        // Search grants (for all, grants_scoped, grants_unscoped)
        if (['all', 'grants_scoped', 'grants_unscoped'].includes(activeTab)) {
          searchPromises.push(
            base('deduped_grants')
              .select({
                filterByFormula: `FIND(LOWER("${searchTerm}"), LOWER({Name}))`,
                maxRecords: 10
              })
              .all()
          );
        } else {
          searchPromises.push(Promise.resolve([]));
        }

        // Search discounts from "discounts copy" (same as VendorSavingsAdminPage)
        if (['all', 'software_savings'].includes(activeTab)) {
          searchPromises.push(
            base(discountsTableId)
              .select({
                filterByFormula: `OR(
                  FIND(LOWER("${searchTerm}"), LOWER({Title})),
                  FIND(LOWER("${searchTerm}"), LOWER({Description})),
                  FIND(LOWER("${searchTerm}"), LOWER({Value})),
                  FIND(LOWER("${searchTerm}"), LOWER({Source})),
                  FIND(LOWER("${searchTerm}"), LOWER({Category}))
                )`,
                maxRecords: 10,
                sort: [{ field: 'MaxSavings', direction: 'desc' }]
              })
              .all()
          );
        } else {
          searchPromises.push(Promise.resolve([]));
        }

        const [companiesRecords, grantsRecords, discountsRecords] = await Promise.all(searchPromises);

        setCompanies(Array.isArray(companiesRecords) ? companiesRecords : []);
        setGrants(Array.isArray(grantsRecords) ? grantsRecords : []);
        setDiscounts(Array.isArray(discountsRecords) ? discountsRecords : []);
      } catch (error) {
        console.error('Error searching:', error);
        setDiscounts([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(searchData, 300);
    return () => clearTimeout(timeoutId);
  }, [query, activeTab]);

  const handleSelect = (item) => {
    if (typeof item.onSelect === 'function') {
      item.onSelect(navigate);
    } else {
      window.location.href = getItemUrl(item);
    }
    onClose();
    setQuery('');
  };

  // Build results based on active tab
  const allResults = React.useMemo(() => {
    const items = [];
    const showCompanies = ['all', 'companies'].includes(activeTab);
    const showGrantsScoped = ['all', 'grants_scoped'].includes(activeTab);
    const showGrantsUnscoped = ['all', 'grants_unscoped'].includes(activeTab);
    const showSoftwareSavings = ['all', 'software_savings'].includes(activeTab);

    if (query.length >= 2) {
      HELP_QUICK_LINKS.forEach((link) => {
        if (matchesQuickLink(link, query)) {
          items.push(link);
        }
      });
    }

    if (admin && query.length >= 2) {
      ADMIN_QUICK_LINKS.forEach((link) => {
        if (matchesQuickLink(link, query)) {
          items.push(link);
        }
      });
    }

    if (admin && (activeTab === 'all' || query.trim().length > 0)) {
      TEAM_ADMIN_PAGES.forEach((link) => {
        if (matchesTeamAdminPage(link, query)) {
          items.push(link);
        }
      });
    }

    if (showCompanies) {
      companies.forEach((company) => {
        const logoUrl = getCompanyLogo(company);
        items.push({
          id: company.id,
          name: company.fields?.Name || 'Unknown Company',
          website: company.fields?.Domain || '',
          icon: BuildingOffice2Icon,
          logoUrl,
          type: 'company',
          destination: 'home',
          resultKey: `company-${company.id}`,
        });
      });
    }

    if (showSoftwareSavings) {
      discounts.forEach((discount) => {
        const title = discount.fields?.Title || 'Unknown';
        const slug = generateSlug(title);
        items.push({
          id: discount.id,
          name: title,
          slug,
          value: discount.fields?.Value,
          maxSavings: discount.fields?.MaxSavings,
          icon: CurrencyDollarIcon,
          type: 'software_saving',
          resultKey: `software-${discount.id}`,
        });
      });
    }

    if (showGrantsScoped) {
      grants.forEach((grant) => {
        items.push({
          id: grant.id,
          name: grant.fields?.Name || 'Unknown Grant',
          companyId: grant.fields?.Company?.[0] || '',
          grantId: grant.id,
          type: 'grant',
          grantScope: 'scoped',
          icon: DocumentTextIcon,
          resultKey: `grant-scoped-${grant.id}`,
        });
      });
    }
    if (showGrantsUnscoped) {
      grants.forEach((grant) => {
        items.push({
          id: grant.id,
          name: grant.fields?.Name || 'Unknown Grant',
          companyId: grant.fields?.Company?.[0] || '',
          grantId: grant.id,
          type: 'grant',
          grantScope: 'unscoped',
          icon: DocumentTextIcon,
          resultKey: `grant-unscoped-${grant.id}`,
        });
      });
    }

    return items;
  }, [activeTab, admin, companies, grants, discounts, query]);

  // Group results by category for display
  const groups = allResults.reduce((acc, item) => {
    let category;
    if (item.type === 'page') {
      category = item.adminPage ? item.section : 'Pages';
    } else if (item.type === 'software_saving') {
      category = 'Software Savings';
    } else if (item.type === 'company') {
      category = 'Companies';
    } else {
      category = item.grantScope === 'scoped' ? 'Grants - PerfectDrafts' : 'Grants';
    }
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  return (
    <Dialog
      ref={dialogRef}
      className="relative z-50"
      open={isOpen}
      onClose={() => {
        debugLog('Dialog onClose called');
        onClose();
        setQuery('');
      }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/25 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in dark:bg-gray-900/50"
        onClick={() => {
          debugLog('DialogBackdrop clicked, closing command palette');
          onClose();
          setQuery('');
        }}
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
            <DialogPanel
              transition
              className="mx-auto max-w-2xl transform overflow-hidden rounded-xl bg-white shadow-2xl outline-1 outline-black/5 transition-all data-closed:scale-95 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in dark:bg-gray-900 dark:-outline-offset-1 dark:outline-white/10"
              onClick={(e) => e.stopPropagation()}
            >
          <Combobox
            onChange={(item) => {
              if (item) {
                handleSelect(item);
              }
            }}
          >
            <div className="grid grid-cols-1">
              <ComboboxInput
                ref={inputRef}
                autoFocus
                className="col-start-1 row-start-1 h-12 w-full bg-transparent pr-4 pl-11 text-base text-gray-900 outline-none border-0 placeholder:text-gray-500 sm:text-sm dark:text-white dark:placeholder:text-gray-400 focus:outline-none focus:ring-0"
                placeholder={admin ? 'Search companies, grants, and team admin...' : 'Search companies and grants...'}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <MagnifyingGlassIcon
                className="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-gray-900/40 dark:text-gray-500"
                aria-hidden="true"
              />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-gray-100 px-4 dark:border-white/10 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2.5 text-xs font-medium rounded-t-md whitespace-nowrap transition-colors focus:outline-none focus:ring-0 ${
                    activeTab === tab.id
                      ? 'bg-slate-100 text-gray-900 dark:bg-slate-700/60 dark:text-white border-b-2 border-slate-300 dark:border-slate-500 -mb-px'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-white/5'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {query === '' && allResults.length === 0 && (
              <div className="border-t border-gray-100 px-6 py-14 text-center text-sm sm:px-14 dark:border-white/10">
                <MagnifyingGlassIcon className="mx-auto size-6 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                <p className="mt-4 font-semibold text-gray-900 dark:text-white">Search for companies and grants</p>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  Use the tabs above to filter by type. Type at least 2 characters to search.
                </p>
              </div>
            )}

            {allResults.length > 0 && (
              <div ref={optionsRef}>
              <ComboboxOptions
                static
                as="ul"
                className="max-h-80 scroll-pt-11 scroll-pb-2 space-y-2 overflow-y-auto pb-2"
              >
                {(() => {
                  let flatIndex = 0;
                  return Object.entries(groups).map(([category, items]) => (
                    <li key={category}>
                      <h2 className="bg-gray-100/50 px-4 py-2.5 text-xs font-semibold text-gray-900 dark:bg-white/5 dark:text-white">
                        {category}
                      </h2>
                      <ul className="mt-2 text-sm text-gray-800 dark:text-gray-300">
                        {items.map((item) => {
                          const optionIndex = flatIndex++;
                          const isHighlighted = optionIndex === highlightedIndex;
                          return (
                        <ComboboxOption
                          key={item.resultKey || `${item.type}-${item.id}`}
                          value={item}
                          className={`group flex cursor-pointer items-center rounded-md px-4 py-2 select-none hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white focus:outline-none focus:ring-0 ${
                            isHighlighted
                              ? '!bg-slate-100 !text-gray-900 ring-1 ring-slate-200 ring-inset dark:!bg-slate-700/60 dark:!text-white dark:ring-slate-600'
                              : 'data-focus:bg-slate-100 data-focus:text-gray-900 data-focus:ring-1 data-focus:ring-slate-200 data-focus:ring-inset data-focus:outline-hidden dark:data-focus:bg-slate-700/60 dark:data-focus:text-white dark:data-focus:ring-slate-600'
                          }`}
                        >
                          {item.type === 'company' && item.logoUrl ? (
                            <img
                              src={item.logoUrl}
                              alt={`${item.name} logo`}
                              className="size-6 flex-none rounded-sm"
                              onError={(e) => {
                                // Fallback to icon if image fails to load
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <item.icon
                            className={`size-6 flex-none text-gray-900/40 group-hover:text-gray-600 group-data-focus:text-gray-700 dark:text-gray-500 dark:group-hover:text-gray-300 dark:group-data-focus:text-white ${
                              item.type === 'company' && item.logoUrl ? 'hidden' : ''
                            } ${isHighlighted ? '!text-gray-700 dark:!text-white' : ''}`}
                            aria-hidden="true"
                          />
                          <span className={`ml-3 flex-auto truncate ${isHighlighted ? '!text-gray-900 dark:!text-white' : ''}`}>{item.name}</span>
                          {item.shortcut ? (
                            <span className="ml-3 inline-flex shrink-0 items-center rounded-md border border-gray-200 bg-white px-1.5 py-0.5 font-sans text-[11px] leading-none text-gray-400">
                              ⌘{String(item.shortcut).toUpperCase()}
                            </span>
                          ) : null}
                          <span className={`ml-3 flex-none text-xs text-gray-500 group-hover:text-gray-600 group-data-focus:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300 dark:group-data-focus:text-white ${isHighlighted ? '!text-gray-600 dark:!text-white' : ''}`}>
                            {item.type === 'page' ? (item.adminPage ? 'Team Admin' : 'Page') : item.type === 'software_saving' ? (item.value || 'Software Savings') : item.type === 'company' ? 'Company' : item.grantScope === 'scoped' ? 'Grant (Scoped)' : 'Grant'}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => handleCopyLink(e, item)}
                            onMouseDown={(e) => e.stopPropagation()}
                            className={`ml-2 flex-none rounded p-1.5 text-gray-400 opacity-0 group-hover:opacity-100 group-data-focus:opacity-100 hover:bg-gray-200 hover:text-gray-600 group-data-focus:bg-slate-200/50 group-data-focus:text-gray-700 dark:group-data-focus:bg-white/20 dark:group-data-focus:text-white transition-opacity focus:outline-none focus:ring-0 ${isHighlighted ? '!opacity-100 !text-gray-600 hover:!bg-slate-200/50 dark:!text-white dark:hover:!bg-white/20' : ''} ${copiedItemId === (item.resultKey || `${item.type}-${item.id}`) ? '!opacity-100' : ''}`}
                            title={copiedItemId === (item.resultKey || `${item.type}-${item.id}`) ? 'Copied!' : 'Copy link'}
                            aria-label={copiedItemId === (item.resultKey || `${item.type}-${item.id}`) ? 'Copied!' : 'Copy link'}
                          >
                            {copiedItemId === (item.resultKey || `${item.type}-${item.id}`) ? (
                              <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                                <CheckIcon className="size-3.5" />
                                Copied
                              </span>
                            ) : (
                              <ArrowDownTrayIcon className="size-4" />
                            )}
                          </button>
                        </ComboboxOption>
                          );
                        })}
                    </ul>
                  </li>
                  ));
                })()}
              </ComboboxOptions>
              </div>
            )}

            {query.length >= 2 && loading && allResults.length === 0 && (
              <div className="px-6 py-14 text-center sm:px-14">
                <div className="text-sm text-gray-500">Searching...</div>
              </div>
            )}

            {query.length >= 2 && allResults.length === 0 && !loading && (
              <div className="border-t border-gray-100 px-6 py-14 text-center text-sm sm:px-14 dark:border-white/10">
                <MagnifyingGlassIcon className="mx-auto size-6 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                <p className="mt-4 font-semibold text-gray-900 dark:text-white">No results found</p>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  We couldn't find anything with that term. Please try again.
                </p>
              </div>
            )}

            {query.length > 0 && query.length < 2 && allResults.length === 0 && (
              <div className="border-t border-gray-100 px-6 py-14 text-center sm:px-14 dark:border-white/10">
                <MagnifyingGlassIcon className="mx-auto size-6 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                <p className="mt-4 font-semibold text-gray-900 dark:text-white">Type at least 2 characters to search</p>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  Start typing to search for companies and grants.
                </p>
              </div>
            )}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default CommandPalette;
